import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { SYSTEM_ROLES, DEFAULT_ROLE_PERMISSIONS } from '../utils/systemPermissions';

/**
 * System Admin Invitation Service
 * Handles invitations for IT professionals to onboard as system admin accounts.
 * These are completely separate from family invitations.
 *
 * Invitations stored at: systemAdminInvitations/{inviteCode}
 */
class SystemAdminInviteService {
  /**
   * Create a system admin invitation (Root Admin only)
   * @param {string} inviterId - Root admin user ID creating the invitation
   * @param {Object} invitationData - Invitation details
   * @param {string} invitationData.inviteeEmail - Email of the IT professional
   * @param {string} invitationData.inviteeName - Name of the IT professional
   * @param {string} invitationData.systemRole - Role to assign (developer, ux_designer, support_agent, etc.)
   * @param {string} [invitationData.customMessage] - Optional welcome message
   * @returns {Promise<Object>} Created invitation with link
   */
  async createInvitation(inviterId, invitationData) {
    if (!inviterId) {
      throw new Error('Inviter ID is required');
    }

    if (!invitationData.inviteeEmail) {
      throw new Error('Invitee email is required');
    }

    if (!invitationData.systemRole) {
      throw new Error('System role is required');
    }

    // Validate the role is a valid system role
    const validRoles = Object.values(SYSTEM_ROLES).filter(r => r !== SYSTEM_ROLES.ROOT_ADMIN);
    if (!validRoles.includes(invitationData.systemRole)) {
      throw new Error(`Invalid system role. Must be one of: ${validRoles.join(', ')}`);
    }

    // Check if there's already a pending invitation for this email
    const existing = await this.getPendingInvitationByEmail(invitationData.inviteeEmail);
    if (existing) {
      throw new Error('There is already a pending invitation for this email address');
    }

    // Generate unique invitation code
    const inviteCode = `SA-${nanoid(12).toUpperCase()}`;

    // Get inviter details
    const inviterRef = doc(db, 'users', inviterId);
    const inviterDoc = await getDoc(inviterRef);
    const inviterData = inviterDoc.exists() ? inviterDoc.data() : {};

    // Get default permissions for the role
    const defaultPermissions = DEFAULT_ROLE_PERMISSIONS[invitationData.systemRole] || {};

    const invitation = {
      inviteCode,
      type: 'system_admin',
      inviterId,
      inviterName: inviterData.displayName || inviterData.email || 'Root Admin',
      inviterEmail: inviterData.email,

      // Invitee details
      inviteeEmail: invitationData.inviteeEmail.toLowerCase().trim(),
      inviteeName: invitationData.inviteeName || null,

      // Role assignment
      systemRole: invitationData.systemRole,
      systemPermissions: defaultPermissions,

      // Message
      customMessage: invitationData.customMessage || '',

      // Status tracking
      status: 'pending', // pending, accepted, declined, expired, revoked
      sentAt: new Date(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days (shorter than family invites)
      acceptedAt: null,
      acceptedBy: null,

      // Metadata
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const inviteRef = doc(db, 'systemAdminInvitations', inviteCode);
    await setDoc(inviteRef, invitation);

    // Generate invitation link
    const inviteLink = `${window.location.origin}/system-join/${inviteCode}`;

    return {
      ...invitation,
      inviteLink
    };
  }

  /**
   * Get a system admin invitation by code
   * @param {string} inviteCode - Invitation code
   * @returns {Promise<Object>} Invitation data
   */
  async getInvitation(inviteCode) {
    if (!inviteCode) {
      throw new Error('Invitation code is required');
    }

    const inviteRef = doc(db, 'systemAdminInvitations', inviteCode);
    const inviteDoc = await getDoc(inviteRef);

    if (!inviteDoc.exists()) {
      throw new Error('System admin invitation not found');
    }

    const invitationData = inviteDoc.data();

    // Check if expired
    const now = new Date();
    const expiresAt = invitationData.expiresAt?.toDate
      ? invitationData.expiresAt.toDate()
      : new Date(invitationData.expiresAt);

    if (now > expiresAt && invitationData.status === 'pending') {
      await updateDoc(inviteRef, {
        status: 'expired',
        updatedAt: new Date()
      });
      throw new Error('Invitation has expired');
    }

    return {
      id: inviteDoc.id,
      ...invitationData
    };
  }

  /**
   * Get pending invitation by email
   * @param {string} email - Email to check
   * @returns {Promise<Object|null>} Pending invitation or null
   */
  async getPendingInvitationByEmail(email) {
    const normalizedEmail = email.toLowerCase().trim();
    const invitationsRef = collection(db, 'systemAdminInvitations');
    const q = query(
      invitationsRef,
      where('inviteeEmail', '==', normalizedEmail),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  }

  /**
   * Accept a system admin invitation and mark the user as a system admin
   * Called after the user has created their auth account
   * @param {string} inviteCode - Invitation code
   * @param {string} userId - Firebase Auth UID of the new user
   * @returns {Promise<Object>} Role and permissions assigned
   */
  async acceptInvitation(inviteCode, userId) {
    if (!inviteCode || !userId) {
      throw new Error('Invitation code and user ID are required');
    }

    const invitation = await this.getInvitation(inviteCode);

    if (invitation.status !== 'pending') {
      throw new Error(`Invitation is ${invitation.status}`);
    }

    // Update invitation status
    const inviteRef = doc(db, 'systemAdminInvitations', inviteCode);
    await updateDoc(inviteRef, {
      status: 'accepted',
      acceptedBy: userId,
      acceptedAt: new Date(),
      updatedAt: new Date()
    });

    return {
      systemRole: invitation.systemRole,
      systemPermissions: invitation.systemPermissions
    };
  }

  /**
   * Revoke a system admin invitation (Root Admin only)
   * @param {string} inviteCode - Invitation code
   * @param {string} revokerId - User ID revoking
   * @returns {Promise<void>}
   */
  async revokeInvitation(inviteCode, revokerId) {
    if (!inviteCode || !revokerId) {
      throw new Error('Invitation code and revoker ID are required');
    }

    const inviteRef = doc(db, 'systemAdminInvitations', inviteCode);
    const inviteDoc = await getDoc(inviteRef);

    if (!inviteDoc.exists()) {
      throw new Error('Invitation not found');
    }

    const invitation = inviteDoc.data();
    if (invitation.status !== 'pending') {
      throw new Error('Can only revoke pending invitations');
    }

    await updateDoc(inviteRef, {
      status: 'revoked',
      revokedBy: revokerId,
      revokedAt: new Date(),
      updatedAt: new Date()
    });
  }

  /**
   * Resend a system admin invitation (extends expiration)
   * @param {string} inviteCode - Invitation code
   * @param {string} resenderId - User ID resending
   * @returns {Promise<Object>} Updated invitation
   */
  async resendInvitation(inviteCode, resenderId) {
    if (!inviteCode || !resenderId) {
      throw new Error('Invitation code and resender ID are required');
    }

    const inviteRef = doc(db, 'systemAdminInvitations', inviteCode);
    const inviteDoc = await getDoc(inviteRef);

    if (!inviteDoc.exists()) {
      throw new Error('Invitation not found');
    }

    const invitation = inviteDoc.data();
    if (!['pending', 'expired'].includes(invitation.status)) {
      throw new Error('Can only resend pending or expired invitations');
    }

    const updates = {
      status: 'pending',
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      resentAt: new Date(),
      resentBy: resenderId,
      updatedAt: new Date()
    };

    await updateDoc(inviteRef, updates);

    return { id: inviteCode, ...invitation, ...updates };
  }

  /**
   * Get all system admin invitations
   * @param {Object} [filters] - Optional filters
   * @param {string} [filters.status] - Filter by status
   * @returns {Promise<Array>} Array of invitations
   */
  async getAllInvitations(filters = {}) {
    const invitationsRef = collection(db, 'systemAdminInvitations');
    let q;

    if (filters.status) {
      q = query(
        invitationsRef,
        where('status', '==', filters.status),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(invitationsRef, orderBy('createdAt', 'desc'));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get invitation statistics
   * @returns {Promise<Object>} Stats object
   */
  async getInvitationStats() {
    const invitations = await this.getAllInvitations();

    return {
      total: invitations.length,
      pending: invitations.filter(i => i.status === 'pending').length,
      accepted: invitations.filter(i => i.status === 'accepted').length,
      declined: invitations.filter(i => i.status === 'declined').length,
      expired: invitations.filter(i => i.status === 'expired').length,
      revoked: invitations.filter(i => i.status === 'revoked').length,
      byRole: invitations.reduce((acc, inv) => {
        acc[inv.systemRole] = (acc[inv.systemRole] || 0) + 1;
        return acc;
      }, {})
    };
  }
}

export const systemAdminInviteService = new SystemAdminInviteService();
